import { Alert, Badge, Button, Group, Table, Text } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { IconDownload } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api, podatki } from '../api/odjemalec';
import { sporocilo } from '../api/napaka';
import { prenesiDatoteko } from '../api/pdf';
import { datum, datumZaApi, denar, tocke } from '../oblika/oblika';
import { Kartica, StanjeNalaganja } from '../postavitev/Podatki';
import { cas, mesec } from './skrbnistvo';
import { glave, obNapaki } from './zeton';

/**
 * Skrbništvo → Nakazila: nakazila vseh instanc (Stripe in naš TRR) z datumom plačila v obdobju (privzeto tekoči mesec),
 * s stroškom obdelave in stanjem točk prej in potem, za
 * ročno izdajo računov in izvoz CSV za računovodjo (CSV izdela strežnik).
 */
export function Nakazila() {
  const [zacetno] = useState(() => mesec());
  const [od, setOd] = useState<string | null>(zacetno[0]);
  const [doDne, setDo] = useState<string | null>(zacetno[1]);
  const [napaka, setNapaka] = useState<string | null>(null);
  const [izvaza, setIzvaza] = useState(false);
  const obdobje = { od: datumZaApi(od) ?? zacetno[0], do: datumZaApi(doDne) ?? zacetno[1] };
  const nakazila = useQuery({
    queryKey: ['skrbnik', 'nakazila', obdobje],
    queryFn: async ({ signal }) => {
      try {
        return await podatki(
          api.GET('/api/skrbnik/nakazila', { signal, headers: glave(), params: { query: obdobje } }),
        );
      } catch (e) {
        obNapaki(e);
        throw e;
      }
    },
  });
  const izvozi = async () => {
    setIzvaza(true);
    setNapaka(null);
    try {
      await prenesiDatoteko(
        api.GET('/api/skrbnik/nakazila/csv', { headers: glave(), params: { query: obdobje }, parseAs: 'blob' }),
        `nakazila-${obdobje.od}-${obdobje.do}.csv`,
      );
    } catch (e) {
      obNapaki(e);
      setNapaka(sporocilo(e));
    } finally {
      setIzvaza(false);
    }
  };
  const skupaj = (nakazila.data ?? []).reduce((v, n) => v + (n.znesek ?? 0), 0);

  return (
    <Kartica naslov="Nakazila">
      <Group gap="sm" align="flex-end" mb="sm">
        <DatePickerInput label="Od" value={od} onChange={setOd} valueFormat="D. M. YYYY" w={170} />
        <DatePickerInput label="Do" value={doDne} onChange={setDo} valueFormat="D. M. YYYY" w={170} />
        <Button
          variant="default"
          leftSection={<IconDownload size={18} />}
          onClick={() => void izvozi()}
          loading={izvaza}
        >
          Izvozi CSV
        </Button>
      </Group>
      {napaka && (
        <Alert color="red" variant="light" role="alert" mb="sm">
          {napaka}
        </Alert>
      )}
      {!nakazila.data ? (
        <StanjeNalaganja
          napaka={nakazila.error}
          ponovi={() => void nakazila.refetch()}
          naslov="Nakazil ni bilo mogoče naložiti"
        />
      ) : nakazila.data.length === 0 ? (
        <Text size="sm" c="dimmed">
          V tem obdobju ni nakazil.
        </Text>
      ) : (
        <Table.ScrollContainer minWidth={1000}>
          <Table verticalSpacing={6}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Datum plačila</Table.Th>
                <Table.Th>Vir</Table.Th>
                <Table.Th>Instanca</Table.Th>
                <Table.Th ta="right">Znesek (€)</Table.Th>
                <Table.Th ta="right">Bonus</Table.Th>
                <Table.Th ta="right">Strošek</Table.Th>
                <Table.Th ta="right">Stanje prej</Table.Th>
                <Table.Th ta="right">Stanje potem</Table.Th>
                <Table.Th>ID plačila</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {nakazila.data.map((n) => (
                <Table.Tr key={n.placiloId}>
                  <Table.Td className="brez-preloma" title={`Vneseno ${cas(n.cas)}`}>
                    {datum(n.datumPlacila)}
                  </Table.Td>
                  <Table.Td>
                    <Badge variant="light" color={n.vir === 'TRR' ? 'blue' : 'grape'}>
                      {n.vir === 'TRR' ? 'TRR' : 'Stripe'}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    {n.naziv ?? ''}{' '}
                    <Text span size="xs" c="dimmed">
                      #{n.companyId}
                    </Text>
                  </Table.Td>
                  <Table.Td ta="right">{denar(n.znesek)}</Table.Td>
                  <Table.Td ta="right">{n.bonus ? denar(n.bonus) : ''}</Table.Td>
                  <Table.Td ta="right">{n.strosek ? denar(n.strosek) : ''}</Table.Td>
                  <Table.Td ta="right" c="dimmed">
                    {tocke(n.stanjePrej)}
                  </Table.Td>
                  <Table.Td ta="right" fw={500}>
                    {tocke(n.stanjePo)}
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" ff="monospace">
                      {n.placiloId}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
            <Table.Tfoot>
              <Table.Tr>
                <Table.Th colSpan={3}>Skupaj ({nakazila.data.length})</Table.Th>
                <Table.Th ta="right">{denar(skupaj)}</Table.Th>
                <Table.Th colSpan={5} />
              </Table.Tr>
            </Table.Tfoot>
          </Table>
        </Table.ScrollContainer>
      )}
    </Kartica>
  );
}
